<script setup lang="ts">
import BlogBreadcrumb from '@/components/blog/BlogBreadcrumb.vue'
import BlogCard from '@/components/blog/BlogCard.vue'
import BlogFAQ from '@/components/blog/BlogFaq.vue'
import type { Blog } from '@/data/blogs'
import { formatDateToISO, getBlogBySlug, getBlogFolderByCategory } from '@/data/blogs'
import { blogs } from '@/data/blogs'
import { getLatestBlogs } from '@/data/blogs'
import { easeOut, motion } from 'motion-v'

const route = useRoute()

// Get the slug from the URL (e.g., "why-restaurant-marketing-feels-complicated")
const slugFromUrl = route.path.split('/').pop()

const blog = getBlogBySlug(slugFromUrl?.toString() || '')

const lastThreeBlogs = getLatestBlogs(
  blogs.filter(blogItem => blogItem.slug !== slugFromUrl && blogItem.category === blog?.category),
  3,
)

const headlineVariants = {
  initial: { opacity: 0, y: '10%' },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easeOut } },
}

const paragraphVariants = {
  initial: { opacity: 0, y: 20 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.05, duration: 0.4, ease: easeOut },
    }
  },
}

const blogVariants = {
  initial: { opacity: 0, y: 50 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.5, ease: easeOut },
    }
  },
}

const blogSchema = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  'headline': blog?.seo?.title,
  'image': [
    blog?.image,
  ],
  'datePublished': formatDateToISO(blog?.seo?.datePublished || ''),
  // 'dateModified': blog?.seo?.dateModified,
  // 'author': blog?.seo?.author?.map(a => ({
  //   '@type': a['@type'],
  //   'name': a.name,
  //   'url': a.url,
  // })) || [],
}

const faqSchema = computed(() => {
  if (!blog?.faq?.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': blog.faq.map(item => ({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.answer,
      },
    })),
  }
})

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': 'Home',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/`,
    },
    {
      '@type': 'ListItem',
      'position': 2,
      'name': 'Blog',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog`,
    },
    {
      '@type': 'ListItem',
      'position': 3,
      'name': blog?.category,
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog?category=${encodeURIComponent(blog?.category || '')}`,
    },
    {
      '@type': 'ListItem',
      'position': 4,
      'name': blog?.title,
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog/${getBlogFolderByCategory(blog?.category || '')}/${slugFromUrl}`,
    },
  ],
}

useHead({
  link: [{
    rel: 'canonical',
    href: `${import.meta.env.VITE_MONOLITH_URL}/blog/${getBlogFolderByCategory(blog?.category || '')}/${slugFromUrl}`,
  }],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(blogSchema), // BlogPosting
    },
    blog?.faq?.length
      ? {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(faqSchema.value), // FAQPage
        }
      : null,
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(breadcrumbSchema),
    },
  ].filter(Boolean),
})

useSeoMeta({
  title: blog?.seo?.title,
  description: blog?.seo?.description,
  ogTitle: blog?.seo?.title,
  ogDescription: blog?.seo?.description,
  ogImage: blog?.seo?.ogImage,
})
</script>
<template>
  <div class="isolate">
    <LayoutNavbar />

    <div
      class="mb-0 bg-gradient-to-b from-[#f8f8f0] from-50% to-[#f8f8f0] pt-4  md:shadow-2xl md:shadow-black/10"
    >
      <section class="mx-auto mb-8 max-w-screen-xl px-4 md:px-8 lg:max-w-screen-xl xl:px-8">
        <BlogBreadcrumb
          :blog="blog as Blog"
        />
        <motion.h1
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-3xl font-medium text-gray-900 lg:text-4xl"
        >
          {{ blog?.title }}
        </motion.h1>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          <router-link
            :to="{ path: '/blog', query: { category: blog?.category } }"
            class="text-[#FF7118] underline"
          >
            {{ blog?.category }}
          </router-link> • {{ blog?.date }}
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          Key Takeaways (30 Seconds)
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-4 text-left text-slate-700"
        >
          Restaurant marketing feels complicated because most restaurants use multiple disconnected tools that don’t work together.
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>
              Fragmented systems create extra work, inconsistent messaging, and missed revenue opportunities.
            </span>
          </li>
          <li>
            <span>
              A
              <RouterLink :to="{ name: '/how-it-works' }">
                <span class="text-[#FF7118] underline">unified restaurant marketing system connects</span>
              </RouterLink>  your website, SEO, reviews, reservations, and guest data into one platform.
            </span>
          </li>
          <li>
            <span>
              Connected systems improve Google visibility, conversions, and repeat visits automatically.

            </span>
          </li>
          <li>
            <span>
              The best marketing systems focus on outcomes: more reservations, more repeat customers, and predictable revenue.

            </span>
          </li>
        </motion.ul>

        <div class="mb-5 mt-8 flex flex-col items-stretch gap-4 md:flex-row">
          <motion.img
            :initial="{ opacity: 0, y: 50 }"
            :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
            :src="blog?.image"
            :alt="blog?.alt"
            class=" w-full rounded-2xl object-cover drop-shadow-lg md:w-7/12"
          />

          <div class="w-full rounded-2xl border-4 border-[#FF711833] bg-white px-4 py-2 md:w-5/12">
            <motion.p
              :variants="paragraphVariants"
              :custom="0"
              initial="initial"
              while-in-view="animate"
              class="relative w-full text-xl leading-relaxed text-slate-700"
            >
              <svg
                width="35"
                height="28"
                viewBox="0 0 35 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="inline pb-1"
              >
                <path
                  d="M33.7768 1.80041e-06C34.496 1.86329e-06 35 0.546003 35 1.1585C35 1.59425 34.7463 2.0615 34.1303 2.38875C31.0013 4.046 27.3788 9.78425 27.3788 13.3228C32.6568 13.1618 34.9493 17.8413 34.9493 20.7743C34.9493 24.2848 31.9077 28 27.4102 28C21.7385 28 18.9577 23.5427 18.9577 18.7127C18.9578 8.246 30.9173 1.55042e-06 33.7768 1.80041e-06ZM14.819 1.43069e-07C15.5365 2.05795e-07 16.0423 0.546002 16.0423 1.1585C16.0423 1.59425 15.7885 2.0615 15.1725 2.38875C12.0435 4.046 8.421 9.78425 8.421 13.3227C13.6972 13.1617 15.9915 17.8412 15.9915 20.7742C15.9915 24.2847 12.9482 28 8.4525 28C2.78075 28 -3.42503e-06 23.5427 -3.00278e-06 18.7127C-2.08775e-06 8.246 11.9578 -1.07069e-07 14.819 1.43069e-07Z"
                  fill="#FF7118"
                />
              </svg>
              <span class="">
                Lower visibility leads to fewer website visitors, which leads to fewer reservations. That’s not just a marketing problem. It’s a revenue problem.
              </span>
              <svg
                width="35"
                height="28"
                viewBox="0 0 35 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="inline pt-1"
              >
                <path
                  d="M1.22325 28C0.504 28 0 27.454 0 26.8415C0 26.4057 0.25375 25.9385 0.86975 25.6112C3.99875 23.954 7.62125 18.2157 7.62125 14.6772C2.34325 14.8382 0.0507501 10.1587 0.0507501 7.22575C0.0507501 3.71525 3.09225 0 7.58975 0C13.2615 0 16.0423 4.45725 16.0423 9.28725C16.0423 19.754 4.08275 28 1.22325 28ZM20.181 28C19.4635 28 18.9578 27.454 18.9578 26.8415C18.9578 26.4057 19.2115 25.9385 19.8275 25.6112C22.9565 23.954 26.579 18.2157 26.579 14.6772C21.3028 14.8382 19.0085 10.1587 19.0085 7.22575C19.0085 3.71525 22.0518 0 26.5475 0C32.2193 0 35 4.45725 35 9.28725C35 19.754 23.0423 28 20.181 28Z"
                  fill="#FF7118"
                />
              </svg>
            </motion.p>
          </div>
        </div>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 pt-6 text-slate-700"
        >
          Restaurant marketing feels complicated because most restaurants rely on disconnected tools that don’t work together.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          You’re updating your website in one place. Managing reservations somewhere else. Responding to Google reviews. Sending emails. Posting on social media.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          It’s not that you’re behind. It’s that your system is fragmented.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Meanwhile, diners are making decisions in seconds. If your website is slow, your Google listing is outdated, or booking isn’t seamless, they move on.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Restaurant marketing shouldn’t feel like a second full-time job.
          And it doesn’t have to.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          Why Is Restaurant Marketing So Complicated?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Restaurant marketing feels complicated because it is spread across multiple disconnected platforms that require constant manual effort.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Most restaurants use separate tools for:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li><span>Website management</span></li>
          <li>
            <span>
              Online reservations
            </span>
          </li>
          <li>
            <span>
              Google Business Profile

            </span>
          </li>
          <li>
            <span>
              Reviews and reputation management
            </span>
          </li>
          <li>
            <span>
              Email and text marketing

            </span>
          </li>
          <li>
            <span>
              Social media and promotions

            </span>
          </li>
          <li>
            <span>
              POS and guest data

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          This creates problems like:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li><span>Duplicate work across platforms</span></li>
          <li>
            <span>
              Inconsistent menus, hours, and information
            </span>
          </li>
          <li>
            <span>
              Missed marketing opportunities

            </span>
          </li>
          <li>
            <span>
              Unclear performance tracking
            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Instead of running one system, you’re managing multiple tasks—and that complexity adds up quickly.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          What Is a Restaurant Marketing System?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          <RouterLink :to="{ name: '/how-it-works' }">
            <span class="text-[#FF7118] underline">A restaurant marketing system is a connected platform</span>
          </RouterLink>
          that integrates your website, SEO, reservations, reviews, and guest communication into one unified system.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          A complete system connects:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Restaurant website
            </span>
          </li>
          <li>
            <span>Search engine optimization (SEO)
            </span>
          </li>
          <li>
            <span>Google Business Profile
            </span>
          </li>
          <li>
            <span>Online reservations and ordering
            </span>
          </li>
          <li>
            <span>Review management
            </span>
          </li>
          <li>
            <span>Email and text marketing
            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Instead of reacting constantly, your marketing runs continuously in the background, attracting new guests and bringing past guests back.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          How Do Disconnected Tools Hurt Restaurant SEO and Revenue?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Disconnected tools hurt restaurant SEO because inconsistent information lowers your visibility in Google search results.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          When your systems don’t connect:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Menus and hours become inconsistent
            </span>
          </li>
          <li>
            <span>Google loses trust in your data
            </span>
          </li>
          <li>
            <span>Rankings drop in local search results
            </span>
          </li>
          <li>
            <span>Fewer diners discover your restaurant
            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          The result:
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Less visibility → fewer website visits → fewer reservations → lost revenue
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          When your digital presence isn’t aligned, your marketing doesn’t just become inefficient—it becomes ineffective.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          How Does a Restaurant Website Drive Revenue?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          A restaurant website drives revenue by converting visitors into reservations, orders, and inquiries.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          A high-performing website should:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Make reservations and ordering easy
            </span>
          </li>
          <li>
            <span>Display menu, hours, and location clearly

            </span>
          </li>
          <li>
            <span>Capture guest data automatically

            </span>
          </li>
          <li>
            <span>Integrate with Google and SEO systems

            </span>
          </li>
          <li>
            <span>Encourage reviews and repeat visits

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          When connected to your marketing system, your website becomes your most powerful revenue channel—not just an online menu.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          How Do You Know If Your Restaurant Marketing Is Working?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Restaurant marketing is working when it consistently turns visibility into measurable revenue.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Key indicators include:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>More Google search impressions and clicks
            </span>
          </li>
          <li>
            <span>
              Increased website traffic

            </span>
          </li>
          <li>
            <span>
              Higher reservation and inquiry conversions

            </span>
          </li>
          <li>
            <span>
              Growth in reviews and ratings

            </span>
          </li>
          <li>
            <span>
              More repeat visits from email and text campaigns

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          If your tools aren’t connected, these results are difficult to track—and even harder to improve.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          What Does a Unified Restaurant Marketing System Look Like?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          A unified restaurant marketing system connects every part of your digital presence into one coordinated engine.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Instead of managing tools, you have:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>A website designed to convert

            </span>
          </li>
          <li>
            <span>
              Automatic reservation and guest data capture

            </span>
          </li>
          <li>
            <span>
              Integrated review management

            </span>
          </li>
          <li>
            <span>
              Email and text campaigns running continuously

            </span>
          </li>
          <li>
            <span>
              Consistent Google visibility improvements

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          When everything works together, your marketing becomes predictable—and your revenue becomes more stable.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          Why Does Restaurant Marketing Work Better as a System?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Restaurant marketing works better as a system because diners experience your brand as one seamless journey—not separate channels.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Diners follow this path:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Search on Google

            </span>
          </li>
          <li>
            <span>
              Visit your website

            </span>
          </li>
          <li>
            <span>
              Check reviews

            </span>
          </li>
          <li>
            <span>
              Book a reservation

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          If any step is confusing or disconnected, they leave.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Restaurants that grow consistently:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Rank well on Google

            </span>
          </li>
          <li>
            <span>
              Make booking effortless

            </span>
          </li>
          <li>
            <span>
              Follow up automatically

            </span>
          </li>
          <li>
            <span>
              Turn guests into repeat customers

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          In a digital-first world, the easiest restaurant to discover and book wins.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          Unified Marketing vs. Disconnected Tools: What’s the Difference?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          The difference between unified marketing and disconnected tools is consistency, efficiency, and revenue impact.
        </motion.p>
        <motion.div
          :initial="{ opacity: 0, y: 50 }"
          :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
          class="mb-6 mt-6 max-w-4xl overflow-hidden rounded-2xl border border-gray-200"
        >
          <!-- Header -->
          <div class="grid grid-cols-2 bg-gray-100 font-semibold text-slate-700">
            <div class="border-r border-gray-200 p-4">
              <p class="text-slate-700">
                Disconnected Tools
              </p>
            </div>
            <div class="p-4">
              <p class="text-slate-700">
                Unified Marketing System
              </p>
            </div>
          </div>

          <!-- Rows -->
          <div class="grid grid-cols-2 divide-x divide-gray-200">
            <motion.div
              v-for="(row, index) in [
                ['Multiple platforms to manage', 'One connected system'],
                ['Inconsistent data across channels', 'Consistent, synchronized information'],
                ['Manual updates and repetitive work', 'Automated updates and workflows'],
                ['Limited visibility into performance', 'Clear reporting and insights'],
                ['Missed opportunities for follow-up', 'Automated guest engagement'],
                ['Lower SEO performance', 'Stronger Google rankings'],
              ]"
              :key="index"
              class="contents"
              :variants="paragraphVariants"
              :custom="index"
            >
              <!-- Left -->
              <div class="border-t border-gray-200 p-4">
                <p class="text-slate-700">
                  {{ row[0] }}
                </p>
              </div>

              <!-- Right -->
              <div class="border-l border-t border-gray-200 p-4">
                <p class="font-medium text-slate-700">
                  {{ row[1] }}
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          How to Simplify Restaurant Marketing?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          To simplify restaurant marketing, replace disconnected tools with a unified system that automates visibility, engagement, and conversions.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Follow these steps:
        </motion.p>
        <motion.ol
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          style="list-style: auto;"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside py-4 text-slate-700"
        >
          <li>
            <span>Consolidate your website, SEO, and reservations into one platform

            </span>
          </li>
          <li>
            <span>
              Ensure your Google Business Profile is fully optimized

            </span>
          </li>
          <li>
            <span>
              Automate review collection and responses

            </span>
          </li>
          <li>
            <span>
              Use email and text marketing for repeat engagement

            </span>
          </li>
          <li>
            <span>
              Track performance in one centralized dashboard

            </span>
          </li>
        </motion.ol>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          The goal isn’t to do more marketing—it’s to make your marketing work better.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          How Can You Simplify Your Restaurant Marketing Today?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          If your marketing feels overwhelming, it’s not because you’re doing it wrong. <br>
          It’s because your tools aren’t working together.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          SpotHopper combines:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Restaurant website design

            </span>
          </li>
          <li>
            <span>
              Built-in SEO

            </span>
          </li>
          <li>
            <span>
              Google review management

            </span>
          </li>
          <li>
            <span>
              Email and text marketing

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          All-in-one system designed to:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Increase visibility

            </span>
          </li>
          <li>
            <span>
              Convert more guests

            </span>
          </li>
          <li>
            <span>
              Fill slow nights

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          If you’re curious about how ShopHopper’s AI marketing can help your restaurant, <RouterLink :to="{ name: '/free-demo' }">
            <span class="text-[#FF7118] underline"> book a free 15-minute demo</span>
          </RouterLink>  and see how marketing can finally feel simple—and effective.
        </motion.p>
      </section>
      <div class="mb-8 w-full bg-white  pb-8 pt-8 md:rounded-[5rem] ">
        <BlogFAQ :items="blog?.faq" />
        <section
          v-if="lastThreeBlogs.length"
          class="mx-auto mb-8 px-2 lg:max-w-screen-xl 2xl:px-0"
        >
          <motion.h2
            :variants="headlineVariants"
            initial="initial"
            while-in-view="animate"
            class="px-4 pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl xl:px-0"
          >
            Keep learning about <span class="lowercase">{{ blog.category }}</span>
          </motion.h2>
          <div class="grid w-full grid-cols-1 gap-x-6 gap-y-12 px-4 py-6 md:grid-cols-3 xl:px-0">
            <motion.div
              v-for="(item, index) in lastThreeBlogs"
              :key="item.slug"
              :custom="index"
              :variants="blogVariants"
              initial="initial"
              while-in-view="animate"
            >
              <BlogCard v-bind="item" />
            </motion.div>
          </div>
        </section>
        <section class="mx-auto px-2 lg:max-w-screen-xl lg:pb-16 2xl:px-0">
          <div class="relative overflow-clip rounded-3xl px-4 py-10 md:px-8 md:py-20">
            <div class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70">
              <motion.img
                :initial="{ opacity: 0, y: 50 }"
                :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
                :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
                class="aspect-video size-full object-cover object-center"
                role="presentation"
              />
            </div>

            <div class="isolate grid place-items-center gap-5">
              <motion.h3
                :variants="headlineVariants"
                initial="initial"
                while-in-view="animate"
                class="max-w-screen-sm text-balance text-center text-3xl leading-tight text-white lg:text-5xl"
              >
                Ready to take your restaurant marketing to the next level?
              </motion.h3>

              <UiButton
                class="group w-full max-w-xs lg:max-w-max"
                as-child
              >
                <RouterLink :to="{ name: '/free-demo' }">
                  Book a Free Demo

                  <UiIcon
                    name="arrow-forward-rounded"
                    class="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </RouterLink>
              </UiButton>
            </div>
          </div>
        </section>
      </div>

      <LayoutFooter />
    </div>
  </div>
</template>
